import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

/**
 * PWAInstallPrompt — subtiele banner die aanbiedt om BPTplus te installeren.
 *
 * Werking:
 * - Chrome/Android/Edge: gebruikt `beforeinstallprompt`-event.
 * - iOS Safari: toont handmatige instructies (Deel → Voeg toe aan beginscherm).
 * - Wordt niet getoond als al geïnstalleerd (`display-mode: standalone`).
 * - Onthoudt dismiss in localStorage voor 7 dagen.
 */
const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_DAYS = 7;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const wasDismissedRecently = () => {
  try {
    const at = localStorage.getItem(DISMISS_KEY);
    if (!at) return false;
    return (Date.now() - Number(at)) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
};

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    const handleBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handleBIP);

    // iOS Safari: show manual guide na 20 sec op de site
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => { setIosMode(true); setVisible(true); }, 20000);
      return () => { window.removeEventListener("beforeinstallprompt", handleBIP); clearTimeout(t); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handleBIP);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    else dismiss();
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div className="pwa-install-banner" data-testid="pwa-install-prompt">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-white/15 flex-shrink-0 flex items-center justify-center">
          <img src="/icon-192.png" alt="BPT+" className="w-9 h-9 rounded-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-snug">BPTplus als app installeren</div>
          {iosMode ? (
            <div className="text-[11px] opacity-90 mt-1 leading-relaxed">
              Tik op <Share className="inline h-3 w-3 mx-0.5" /> in Safari en kies
              &ldquo;Zet op beginscherm&rdquo; om BPTplus als app te gebruiken.
            </div>
          ) : (
            <div className="text-[11px] opacity-90 mt-1">
              Sneller starten · offline melding · fullscreen ervaring
            </div>
          )}
          {!iosMode && (
            <button onClick={install}
                    data-testid="pwa-install-btn"
                    className="mt-2 bg-white text-red-800 px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1">
              <Download className="h-3.5 w-3.5" /> Installeer nu
            </button>
          )}
        </div>
        <button onClick={dismiss} data-testid="pwa-dismiss" aria-label="Sluiten"
                className="p-1 rounded-full hover:bg-white/10 flex-shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
